import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material'
import React from 'react'
import GrantContent from '../../../components/GrantContent'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { DESCRIPCION_MAX, NOMBRE_MAX, useTaskFormModel } from './task-form-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }
const dialogBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'

const TaskForm = () => {
    const model = useTaskFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar tarea</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la tarea {form.nombre}? Esta acción es irreversible.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={`${dialogBtnClass} border border-k-line bg-k-surface text-k-ink hover:bg-k-hover`} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={`${dialogBtnClass} border-0 bg-k-danger font-bold !text-white hover:brightness-110`} onClick={model.deleteTask} disabled={model.deleting}>Eliminar</button>
                </DialogActions>
            </Dialog>

            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description="Registra pendientes, llamadas, visitas o seguimientos y asígnalos a un vendedor."
                    backTo={model.backTo}
                    backLabel="Tareas"
                    actions={model.isEdit ? (
                        <GrantContent permisos={[Permisos.TAREAS_D]}>
                            <button
                                type="button"
                                className="inline-flex h-10 items-center gap-2 rounded-xl border-0 bg-k-danger px-4 text-sm font-bold !text-white hover:brightness-110 disabled:opacity-40"
                                onClick={model.askDelete}
                                disabled={model.busy}
                            >
                                <DeleteIcon style={{ fontSize: 18 }} />
                                Eliminar
                            </button>
                        </GrantContent>
                    ) : (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Tarea</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth required
                            label="Nombre"
                            helperText={`${form.nombre.length}/${NOMBRE_MAX}`}
                            value={form.nombre}
                            onChange={(event) => change.nombre(event.target.value)}
                            slotProps={{ htmlInput: { maxLength: NOMBRE_MAX } }}
                        />
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            type="date"
                            label="Vencimiento"
                            helperText="Déjalo vacío si la tarea no tiene fecha límite."
                            value={form.vencimiento}
                            onChange={(event) => change.vencimiento(event.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select
                            label="Vendedor"
                            value={form.vendedor}
                            onChange={(event) => change.vendedor(event.target.value)}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu }, inputLabel: { shrink: true } }}
                        >
                            <MenuItem value=""><em>Sin vendedor</em></MenuItem>
                            {model.sellers.map((seller) => (
                                <MenuItem key={seller.id} value={String(seller.id)}>{seller.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Prioridad"
                            value={model.prioridades.length ? form.prioridad : ''}
                            onChange={(event) => change.prioridad(event.target.value)}
                            slotProps={{ select: selectMenu }}
                        >
                            {model.prioridades.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                        </TextField>
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Tipo"
                            value={model.tipos.length ? form.tipo : ''}
                            onChange={(event) => change.tipo(event.target.value)}
                            slotProps={{ select: selectMenu }}
                        >
                            {model.tipos.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                        </TextField>
                        {model.isEdit && (
                            <TextField
                                className="v2-field v2-select" variant="filled" fullWidth select required
                                label="Estatus"
                                value={model.statuses.length ? form.status : ''}
                                onChange={(event) => change.status(event.target.value)}
                                slotProps={{ select: selectMenu }}
                            >
                                {model.statuses.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                            </TextField>
                        )}
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth required multiline minRows={4}
                            label="Descripción"
                            helperText={`${form.descripcion.length}/${DESCRIPCION_MAX}`}
                            value={form.descripcion}
                            onChange={(event) => change.descripcion(event.target.value)}
                            slotProps={{ htmlInput: { maxLength: DESCRIPCION_MAX } }}
                        />
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy || !model.canSave}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar tarea
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default TaskForm
